// Canonical department → color map used by both the live calendar render
// and the schedule generator. Kept in one place so the generator only
// suggests departments that the calendar can color-code.

export const DEPARTMENT_COLORS = {
  'Hawaii': '#22c55e',                    // Green
  'OCC': '#ec4899',                       // Pink
  'Engineering': '#ef4444',               // Red
  'Deck': '#60a5fa',                      // Light Blue
  'N/A': '#7f1d1d',                       // Dark Maroon/Red
  'Weapons': '#d2b48c',                   // Tan
  'Everett': '#14b8a6',                   // Turquoise
  'Edmonds': '#0d9488',                   // Dark turquoise
  'NSW/Navy': '#1e3a8a',                  // Dark Blue
  'Gov Vessel': '#78350f',                // Brown
  'NOAA': '#f97316',                      // Orange
  'Special Course': '#fca5a5',            // Light red
  'Alameda': '#991b1b',                   // Dark Red
  'Boot Camp': '#fbbf24',                 // Yellow
  'Shipyard Boot Camp/Welding': '#78350f' // Brown
};

export const CANONICAL_DEPARTMENTS = Object.keys(DEPARTMENT_COLORS);

const lowerMap = CANONICAL_DEPARTMENTS.reduce((acc, name) => {
  acc[name.toLowerCase()] = name;
  return acc;
}, {});

// Resolve a free-text department string against the canonical list.
// Returns the canonical casing if a match is found, else null.
export const canonicalizeDepartment = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const exact = lowerMap[trimmed.toLowerCase()];
  if (exact) return exact;
  // Try substring match (e.g. "Deck Department" → "Deck")
  const sub = CANONICAL_DEPARTMENTS.find(d =>
    trimmed.toLowerCase().includes(d.toLowerCase())
  );
  return sub || null;
};

// Resolve department data from common session shapes for draft rendering.
// Casing and harmless label suffixes still map to the canonical color values.
export const getDepartmentColor = (item) => {
  const raw = item && (item.departmentName || item.department || item.cr38c_department);
  const department = canonicalizeDepartment(typeof raw === 'string' ? raw : String(raw || ''));
  return department ? DEPARTMENT_COLORS[department] : '#3b82f6';
};

// Keyword → canonical department rules used to auto-assign a department
// when historical data doesn't yield a canonical match. Order matters:
// more specific rules come first so e.g. "Welding" beats a generic match.
const KEYWORD_RULES = [
  [/welding|shipyard\s*boot/i, 'Shipyard Boot Camp/Welding'],
  [/boot\s*camp/i, 'Boot Camp'],
  [/\bnsw\b|navy|naval|seal\b/i, 'NSW/Navy'],
  [/\bnoaa\b/i, 'NOAA'],
  [/government|gov\s*vessel|gov't/i, 'Gov Vessel'],
  [/hawaii|honolulu/i, 'Hawaii'],
  [/everett/i, 'Everett'],
  [/edmonds/i, 'Edmonds'],
  [/alameda/i, 'Alameda'],
  [/\bocc\b/i, 'OCC'],
  [/weapon|gunner|tactical/i, 'Weapons'],
  [/engineer/i, 'Engineering'],
  [/\bdeck\b|able\s*seaman|\bab\s|bridge\s*resource|ecdis|stcw|radar/i, 'Deck']
];

// Infer a canonical department from a course name when no historical
// department is available. Always returns a canonical value (falls back
// to 'N/A' so the session is never left without a color-coded department).
export const inferDepartmentFromName = (courseName) => {
  if (!courseName || typeof courseName !== 'string') return 'N/A';
  const name = courseName.trim();
  if (!name) return 'N/A';

  // Step 1: try direct canonical substring match (e.g. course literally
  // contains "Hawaii" or "Engineering")
  const direct = canonicalizeDepartment(name);
  if (direct) return direct;

  // Step 2: keyword heuristics
  for (const [pattern, dept] of KEYWORD_RULES) {
    if (pattern.test(name)) return dept;
  }

  // Step 3: guaranteed fallback so every session gets a color
  return 'N/A';
};
